import { fetchCurrentUser } from '../../ducks/user.duck';
import {
  denormalisedEntities,
  denormalisedResponseEntities,
  updatedEntities,
} from '../../util/data';
import { getListingShop, getProductId } from '../../util/dataExtractor';
import { urlToFile } from '../../util/editListingHelpers';
import { storableError } from '../../util/errors';
import { entityRefs } from '../../util/genericHelpers';
import { updateShopifyProduct } from '../../util/helperFunctions';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

const RESULT_PAGE_SIZE = 30;

// Action types
const ActionTypes = {
  FETCH_LISTING_REQUEST: 'app/ShopSettingsPage/FETCH_LISTING_REQUEST',
  FETCH_LISTING_SUCCESS: 'app/ShopSettingsPage/FETCH_LISTING_SUCCESS',
  FETCH_LISTING_ERROR: 'app/ShopSettingsPage/FETCH_LISTING_ERROR',

  UPLOAD_IMAGE_REQUEST: 'app/ShopSettingsPage/UPLOAD_IMAGE_REQUEST',
  UPLOAD_IMAGE_SUCCESS: 'app/ShopSettingsPage/UPLOAD_IMAGE_SUCCESS',
  UPLOAD_IMAGE_ERROR: 'app/ShopSettingsPage/UPLOAD_IMAGE_ERROR',

  QUERY_REVIEWS_REQUEST: 'app/ShopSettingsPage/QUERY_REVIEWS_REQUEST',
  QUERY_REVIEWS_SUCCESS: 'app/ShopSettingsPage/QUERY_REVIEWS_SUCCESS',
  QUERY_REVIEWS_ERROR: 'app/ShopSettingsPage/QUERY_REVIEWS_ERROR',

  FETCH_LISTINGS_REQUEST: 'app/ShopSettingsPage/FETCH_LISTINGS_REQUEST',
  FETCH_LISTINGS_SUCCESS: 'app/ShopSettingsPage/FETCH_LISTINGS_SUCCESS',
  FETCH_LISTINGS_ERROR: 'app/ShopSettingsPage/FETCH_LISTINGS_ERROR',

  DISCARD_DRAFT_REQUEST: 'app/ShopSettingsPage/DISCARD_DRAFT_REQUEST',
  DISCARD_DRAFT_SUCCESS: 'app/ShopSettingsPage/DISCARD_DRAFT_SUCCESS',
  DISCARD_DRAFT_ERROR: 'app/ShopSettingsPage/DISCARD_DRAFT_ERROR',

  PUBLISH_DRAFT_REQUEST: 'app/ShopSettingsPage/PUBLISH_DRAFT_REQUEST',
  PUBLISH_DRAFT_SUCCESS: 'app/ShopSettingsPage/PUBLISH_DRAFT_SUCCESS',
  PUBLISH_DRAFT_ERROR: 'app/ShopSettingsPage/PUBLISH_DRAFT_ERROR',

  OPEN_LISTING_REQUEST: 'app/ShopSettingsPage/OPEN_LISTING_REQUEST',
  OPEN_LISTING_SUCCESS: 'app/ShopSettingsPage/OPEN_LISTING_SUCCESS',
  OPEN_LISTING_ERROR: 'app/ShopSettingsPage/OPEN_LISTING_ERROR',

  CLOSE_LISTING_REQUEST: 'app/ShopSettingsPage/CLOSE_LISTING_REQUEST',
  CLOSE_LISTING_SUCCESS: 'app/ShopSettingsPage/CLOSE_LISTING_SUCCESS',
  CLOSE_LISTING_ERROR: 'app/ShopSettingsPage/CLOSE_LISTING_ERROR',

  UPDATE_LISTING_REQUEST: 'app/ShopSettingsPage/UPDATE_LISTING_REQUEST',
  UPDATE_LISTING_SUCCESS: 'app/ShopSettingsPage/UPDATE_LISTING_SUCCESS',
  UPDATE_LISTING_ERROR: 'app/ShopSettingsPage/UPDATE_LISTING_ERROR',

  COPY_LISTING_REQUEST: 'app/ShopSettingsPage/COPY_LISTING_REQUEST',
  COPY_LISTING_SUCCESS: 'app/ShopSettingsPage/COPY_LISTING_SUCCESS',
  COPY_LISTING_ERROR: 'app/ShopSettingsPage/COPY_LISTING_ERROR',
};

// Initial state
const initialState = {
  fetchListingInProgress: false,
  fetchListingError: null,
  listings: [],
  uploadImageError: null,
  uploadInProgress: false,
  reviews: [],
  queryReviewsError: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  ownEntities: {},
  discardingDraft: null,
  discardingDraftError: null,
  publishDraft: null,
  publishDraftError: null,
  closeListing: null,
  closeListingError: null,
  openListing: null,
  openListingError: null,
  updateListing: null,
  updateListingError: null,
  copyListing: null,
  copyListingError: null,
};

// Reducer
const ShopSettingsPageReducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
    case ActionTypes.UPLOAD_IMAGE_REQUEST:
      return { ...state, uploadInProgress: true, uploadImageError: null };
    case ActionTypes.UPLOAD_IMAGE_SUCCESS:
      return { ...state, uploadInProgress: false };
    case ActionTypes.UPLOAD_IMAGE_ERROR:
      return { ...state, uploadInProgress: false, uploadImageError: payload };

    case ActionTypes.QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsError: null };
    case ActionTypes.QUERY_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case ActionTypes.QUERY_REVIEWS_ERROR:
      return { ...state, queryReviewsError: payload };

    case ActionTypes.FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryInProgress: true,
        queryListingsError: null,
        queryParams: payload?.queryParams || null,
      };
    case ActionTypes.FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        listings: payload,
        queryInProgress: false,
      };
    case ActionTypes.FETCH_LISTINGS_ERROR:
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case ActionTypes.DISCARD_DRAFT_REQUEST:
      return {
        ...state,
        discardingDraft: payload.listingId,
        discardingDraftError: null,
      };
    case ActionTypes.DISCARD_DRAFT_SUCCESS:
      return {
        ...state,
        discardingDraft: null,
      };
    case ActionTypes.DISCARD_DRAFT_ERROR:
      return {
        ...state,
        discardingDraft: null,
        discardingDraftError: {
          listingId: state.discardingDraft,
          error: payload,
        },
      };

    case ActionTypes.PUBLISH_DRAFT_REQUEST:
      return {
        ...state,
        publishDraft: payload.listingId,
        publishDraftError: null,
      };
    case ActionTypes.PUBLISH_DRAFT_SUCCESS:
      return {
        ...state,
        publishDraft: null,
      };
    case ActionTypes.PUBLISH_DRAFT_ERROR:
      return {
        ...state,
        publishDraft: null,
        publishDraftError: payload,
      };

    case ActionTypes.CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closeListing: payload.listingId,
        closeListingError: null,
      };
    case ActionTypes.CLOSE_LISTING_SUCCESS:
      return {
        ...state,
        closeListing: null,
      };
    case ActionTypes.CLOSE_LISTING_ERROR:
      return {
        ...state,
        closeListing: null,
        closeListingError: payload,
      };

    case ActionTypes.OPEN_LISTING_REQUEST:
      return {
        ...state,
        openListing: payload.listingId,
        openListingError: null,
      };
    case ActionTypes.OPEN_LISTING_SUCCESS:
      return {
        ...state,
        openListing: null,
      };
    case ActionTypes.OPEN_LISTING_ERROR:
      return {
        ...state,
        openListing: null,
        openListingError: payload,
      };

    case ActionTypes.UPDATE_LISTING_REQUEST:
      return {
        ...state,
        updateListing: payload.listingId,
        updateListingError: null,
      };
    case ActionTypes.UPDATE_LISTING_SUCCESS:
      return {
        ...state,
        updateListing: null,
      };
    case ActionTypes.UPDATE_LISTING_ERROR:
      return {
        ...state,
        updateListing: null,
        updateListingError: payload,
      };

    case ActionTypes.COPY_LISTING_REQUEST:
      return {
        ...state,
        copyListing: payload.listingId,
        copyListingError: null,
      };
    case ActionTypes.COPY_LISTING_SUCCESS:
      return {
        ...state,
        copyListing: null,
      };
    case ActionTypes.COPY_LISTING_ERROR:
      return {
        ...state,
        copyListing: null,
        copyListingError: payload,
      };
    default:
      return state;
  }
};

// Selectors
export const uploadImageInProgressSelector = state => state.ShopSettingsPage.uploadInProgress;

export const reviewsSelector = state => state.ShopSettingsPage.reviews;

export const listingsSelector = state => state.ShopSettingsPage.listings;

export const queryInProgressSelector = state => state.ShopSettingsPage.queryInProgress;

export const queryParamsSelector = state => state.ShopSettingsPage.queryParams;

export const copyListingSelector = state => state.ShopSettingsPage.copyListing;

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ShopSettingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// Action creators
export const queryListingsRequest = queryParams => ({
  type: ActionTypes.FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: ActionTypes.FETCH_LISTINGS_SUCCESS,
  payload: response,
});

export const queryListingsError = e => ({
  type: ActionTypes.FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const uploadImageRequest = () => ({ type: ActionTypes.UPLOAD_IMAGE_REQUEST });
const uploadImageSuccess = () => ({ type: ActionTypes.UPLOAD_IMAGE_SUCCESS });
const uploadImageError = error => ({
  type: ActionTypes.UPLOAD_IMAGE_ERROR,
  error: true,
  payload: error,
});

const queryReviewsRequest = () => ({ type: ActionTypes.QUERY_REVIEWS_REQUEST });
const queryReviewsSuccess = reviews => ({
  type: ActionTypes.QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});
const queryReviewsError = error => ({
  type: ActionTypes.QUERY_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const discardDraftRequest = listingId => ({
  type: ActionTypes.DISCARD_DRAFT_REQUEST,
  payload: { listingId },
});

export const discardDraftSuccess = () => ({
  type: ActionTypes.DISCARD_DRAFT_SUCCESS,
});

export const discardDraftError = e => ({
  type: ActionTypes.DISCARD_DRAFT_ERROR,
  error: true,
  payload: e,
});

export const publishDraftRequest = listingId => ({
  type: ActionTypes.PUBLISH_DRAFT_REQUEST,
  payload: { listingId },
});

export const publishDraftSuccess = () => ({
  type: ActionTypes.PUBLISH_DRAFT_SUCCESS,
});

export const publishDraftError = e => ({
  type: ActionTypes.PUBLISH_DRAFT_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: ActionTypes.CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = () => ({
  type: ActionTypes.CLOSE_LISTING_SUCCESS,
});

export const closeListingError = e => ({
  type: ActionTypes.CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const openListingRequest = listingId => ({
  type: ActionTypes.OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = () => ({
  type: ActionTypes.OPEN_LISTING_SUCCESS,
});

export const openListingError = e => ({
  type: ActionTypes.OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const updateListingRequest = listingId => ({
  type: ActionTypes.UPDATE_LISTING_REQUEST,
  payload: { listingId },
});

export const updateListingSuccess = () => ({
  type: ActionTypes.UPDATE_LISTING_SUCCESS,
});

export const updateListingError = e => ({
  type: ActionTypes.UPDATE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const copyListingRequest = listingId => ({
  type: ActionTypes.COPY_LISTING_REQUEST,
  payload: { listingId },
});

export const copyListingSuccess = () => ({
  type: ActionTypes.COPY_LISTING_SUCCESS,
});

export const copyListingError = e => ({
  type: ActionTypes.COPY_LISTING_ERROR,
  error: true,
  payload: e,
});

// Thunks
export const uploadImage = actionPayload => {
  return async (dispatch, getState, sdk) => {
    const { file, isCoverImage = false, isProfileImage = false } = actionPayload;

    if (!file) {
      throw new Error('No file provided for upload');
    }

    dispatch(uploadImageRequest());

    const bodyParams = { image: file };
    const queryParams = {
      expand: true,
    };

    try {
      const resp = await sdk.images.upload(bodyParams, queryParams);
      const {
        data: { data: uploadedImage },
      } = resp;

      if (!uploadedImage) {
        throw new Error('Upload response contains no image data');
      }

      let imageUrl = uploadedImage.attributes?.variants?.default?.url;
      if (!imageUrl) {
        throw new Error('No valid URL found in uploaded image variants');
      }

      if (isCoverImage && !isProfileImage) {
        await dispatch(
          updateProfile({
            publicData: { coverImage: imageUrl },
          })
        );
        dispatch(uploadImageSuccess());
        return imageUrl;
      }

      if (isProfileImage && !isCoverImage) {
        const profileImageId = uploadedImage.id;
        await dispatch(
          updateProfile({
            profileImageId,
          })
        );
        dispatch(uploadImageSuccess());
        return imageUrl;
      }

      throw new Error('Must specify exactly one of isCoverImage or isProfileImage');
    } catch (e) {
      const error = storableError(e);
      dispatch(uploadImageError(error));
      throw error;
    }
  };
};

export const queryUserReviews = userId => {
  return async (dispatch, getState, sdk) => {
    dispatch(queryReviewsRequest());
    try {
      const response = await sdk.reviews.query({
        subject_id: userId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      });

      const reviews = denormalisedResponseEntities(response);
      dispatch(queryReviewsSuccess(reviews));
    } catch (e) {
      dispatch(queryReviewsError(storableError(e)));
    }
  };
};

export const queryOwnListings = (params = {}) => async (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(params));

  try {
    const {
      config,
      page = 1,
      perPage = RESULT_PAGE_SIZE,
      states = 'published,closed,draft',
      fetchAll = false,
      maxPages,
      ...rest
    } = params;

    const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
      config?.layout?.listingImage || {};
    const aspectRatio = aspectHeight / aspectWidth;

    const baseQuery = {
      page,
      perPage,
      states,
      include: ['images', 'currentStock', 'author'],
      'fields.image': [
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        'variants.default',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'fields.listing': ['title', 'description', 'price', 'deleted', 'state', 'publicData'],
      ...rest,
    };

    const listings = [];
    let entities = {};
    let currentPage = page;
    let totalPages = 1;

    const firstRes = await sdk.ownListings.query(baseQuery);
    const { data, meta } = firstRes.data || {};
    totalPages = meta?.totalPages || 1;

    entities = updatedEntities({}, firstRes.data);
    listings.push(...denormalisedEntities(entities, entityRefs(data || []), false));

    if (fetchAll) {
      const lastPage = !!maxPages ? Math.min(totalPages, page + maxPages - 1) : totalPages;
      for (currentPage = page + 1; currentPage <= lastPage; currentPage++) {
        const res = await sdk.ownListings.query({ ...baseQuery, page: currentPage });
        entities = updatedEntities(entities, res.data);
        listings.push(...denormalisedEntities(entities, entityRefs(res.data?.data || []), false));
        await new Promise(r => setTimeout(r, 150));
      }
    }

    dispatch(queryListingsSuccess(listings));
    return listings;
  } catch (err) {
    dispatch(queryListingsError(storableError(err)));
    throw err;
  }
};

/**
 * NEW THUNK:
 * After profile country update, update country for all own listings (publicData.country)
 */
export const updateCountryAndListings = ({ profile, country, isEUCountry }) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    // 1. Update user profile first
    await dispatch(updateProfile(profile));

    // 2. Get current listings from state; if empty, fetch all own listings
    let state = getState();
    let listings = state.ShopSettingsPage?.listings || [];

    if (!listings.length) {
      await dispatch(
        queryOwnListings({
          states: 'published,closed,draft',
          page: 1,
          perPage: RESULT_PAGE_SIZE,
          fetchAll: true,
        })
      );
      state = getState();
      listings = state.ShopSettingsPage?.listings || [];
    }

    if (!listings.length) {
      return;
    }

    // 3. Update publicData.country for each listing (backend only)
    const updatePromises = listings
      .map(listing => {
        const currentPublicData = listing?.attributes?.publicData || {};
        const currentCountry = currentPublicData.country;

        // Skip if already matching to avoid useless calls
        if (currentCountry === country) {
          return null;
        }

        const publicData = {
          ...currentPublicData,
          country: country || null,
          isEUCountry,
        };

        const id = listing.id;

        return sdk.ownListings.update({ id, publicData });
      })
      .filter(Boolean);

    if (updatePromises.length) {
      await Promise.all(updatePromises);

      // 4. Refetch listings so Redux state reflects new country
      await dispatch(
        queryOwnListings({
          states: 'published,closed,draft',
          page: 1,
          perPage: RESULT_PAGE_SIZE,
          fetchAll: true,
        })
      );
    }
  } catch (e) {
    // You can choose to log or surface this; for now we just throw so the caller can handle it
    // eslint-disable-next-line no-console
    console.error('Failed to update country on profile/listings', e);
    throw e;
  }
};

export const discardDraft = (listingId, config) => async (dispatch, getState, sdk) => {
  try {
    dispatch(discardDraftRequest(listingId));
    await sdk.ownListings.discardDraft({ id: listingId }, { expand: true });
    dispatch(discardDraftSuccess());
  } catch (e) {
    dispatch(discardDraftError(storableError(e)));
    throw e;
  }
};

export const publishDraft = (listingId, config) => async (dispatch, getState, sdk) => {
  try {
    dispatch(publishDraftRequest(listingId));
    await sdk.ownListings.publishDraft({ id: listingId }, { expand: true });

    dispatch(publishDraftSuccess());
  } catch (e) {
    dispatch(publishDraftError(storableError(e)));
    throw e;
  }
};

export const closeListing = (listingId, config) => async (dispatch, getState, sdk) => {
  try {
    dispatch(closeListingRequest(listingId));
    await sdk.ownListings.close({ id: listingId }, { expand: true });

    dispatch(closeListingSuccess());
  } catch (e) {
    dispatch(closeListingError(storableError(e)));
    throw e;
  }
};

export const openListing = (listingId, config) => async (dispatch, getState, sdk) => {
  try {
    dispatch(openListingRequest(listingId));
    await sdk.ownListings.open({ id: listingId }, { expand: true });
    dispatch(openListingSuccess());
  } catch (e) {
    dispatch(openListingError(storableError(e)));
    throw e;
  }
};

export const updateListing = params => async (dispatch, getState, sdk) => {
  const { listingId, config, isShopifyAccountPage = false, currentUserId, ...rest } = params || {};

  // Basic validation
  if (!listingId) {
    const errorMsg = 'Missing required listingId';
    dispatch(updateListingError(new Error(errorMsg)));
    return;
  }

  try {
    dispatch(updateListingRequest(listingId));

    // Update the listing in Sharetribe
    const response = await sdk.ownListings.update({ id: listingId, ...rest }, { expand: true });

    // // Handle Shopify product update if applicable
    if (isShopifyAccountPage && currentUserId) {
      try {
        const {
          data: { data: updatedListing },
        } = response || {};
        const shop = getListingShop(updatedListing);
        const productId = getProductId(updatedListing);

        if (shop && productId) {
          await updateShopifyProduct({
            shop,
            productId,
            userId: currentUserId,
            title: params.title,
            description: params.description,
            price: params.price,
          });
        }
      } catch (shopifyError) {}
    }

    dispatch(updateListingSuccess());
  } catch (e) {
    const error = storableError(e);

    dispatch(updateListingError(error));
  }
};

export const copyListing = listing => async (dispatch, getState, sdk) => {
  const listingId = listing?.id?.uuid;
  dispatch(copyListingRequest(listingId));
  try {
    const { attributes, images = [] } = listing || {};
    const { publicData, title, description, price } = attributes || {};

    // Loop over the images and upload new ones
    const uploadPromises = images.map(async (image, index) => {
      const defaultUrl = image.attributes.variants.default.url;
      const defaultName = image.attributes.variants.default.name;
      const filename = `${defaultName}_${image?.id?.uuid}.jpg`;
      const file = await urlToFile(defaultUrl, filename);
      const response = await sdk.images.upload({ image: file }, { expand: true });
      return response.data.data.id.uuid;
    });

    const uploadedImageIds = await Promise.all(uploadPromises);
    const updatedValues = {
      title,
      description,
      price,
      images: uploadedImageIds,
      publicData: {
        ...publicData,
      },
    };

    const response = await sdk.ownListings.createDraft(updatedValues, {
      expand: true,
    });
    const copyListing = response?.data?.data;
    dispatch(copyListingSuccess());
    return copyListing;
  } catch (e) {
    const error = storableError(e);
    dispatch(copyListingError(error));
    throw e;
  }
};

// Thunks
export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { listingState, page = 1, searchTerm, ...rest } = queryParams || {};
  const currentUser = await dispatch(fetchCurrentUser({ enforce: true }));
  const currentUserId = currentUser?.id;
  const listingsPromise = dispatch(
    queryOwnListings({
      config,
      states: 'published,closed,draft',
      page,
      perPage: RESULT_PAGE_SIZE,
      fetchAll: true,
    })
  );
  const promises = [listingsPromise];
  if (currentUserId?.uuid) {
    promises.push(dispatch(queryUserReviews(currentUserId)));
  }
  await Promise.all(promises);
  return currentUser;
};

export default ShopSettingsPageReducer;
